import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, Button, Form, Table } from "react-bootstrap";
import WorkspacePage from "../WorkspacePage";
import { link, numb2 } from "../link";
import "./MyMembership.css";

const contributionRows = [
  ["Capital / shares", "capitalAmount", "capitalPaidAmount", "capitalBalanceAmount"],
  ["Membership", "membershipContributionAmount", "membershipPaidAmount", "membershipBalanceAmount"],
  ["Savings", "savingContributionAmount", "savingPaidAmount", "savingBalanceAmount"],
  ["Commission", "commissionAmount", "commissionPaidAmount", "commissionBalanceAmount"],
];
const penaltyRows = [
  ["Capital penalties", "capitalPenaltyAmount", "capitalPenaltyPaidAmount", "capitalPenaltyBalanceAmount"],
  ["Membership penalties", "membershipPenaltyAmount", "membershipPenaltyPaidAmount", "membershipPenaltyBalanceAmount"],
  ["Savings penalties", "savingPenaltyAmount", "savingPenaltyPaidAmount", "savingPenaltyBalanceAmount"],
  ["Commission penalties", "commissionPenaltyAmount", "commissionPenaltyPaidAmount", "commissionPenaltyBalanceAmount"],
];

function Amounts({ member, rows, title }) {
  return (
    <div className="membership-table-wrap">
      <Table responsive striped className="membership-amounts">
        <caption>{title} ({member.currency || "Currency not specified"})</caption>
        <thead><tr><th scope="col">Item</th><th scope="col">Amount</th><th scope="col">Paid</th><th scope="col">Outstanding</th></tr></thead>
        <tbody>{rows.map(([label, amount, paid, balance]) => (
          <tr key={label}><th scope="row">{label}</th><td>{numb2(member[amount])}</td><td>{numb2(member[paid])}</td><td>{numb2(member[balance])}</td></tr>
        ))}</tbody>
      </Table>
    </div>
  );
}

export default function MyMembership() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [roundID, setRoundID] = useState("");
  const statusOf = (member) => String((member.attachment && member.attachment.roundstatus) || "").trim() || "Not specified";
  const eligibleMemberships = memberships.filter((member) => member.roundID
    && statusOf(member) !== "Not specified" && statusOf(member).toLowerCase() !== "pending");
  const rounds = Array.from(new Map(eligibleMemberships.map((member) => [member.roundID, member])).values())
    .sort((first, second) => Number(statusOf(second) === "Active") - Number(statusOf(first) === "Active"));
  const selectedRoundID = rounds.some((round) => round.roundID === roundID) ? roundID : (rounds[0] ? rounds[0].roundID : "");
  const visibleMemberships = eligibleMemberships.filter((member) => member.roundID === selectedRoundID);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    axios.post(`${link()}&ra=member&do=getown`, {}, { timeout: 30000 })
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Invalid membership response");
        if (active) setMemberships(data);
      })
      .catch(() => { if (active) setError("Your membership information could not be loaded."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);

  return (
    <WorkspacePage title="My Membership" className="membership-page" actions={
          <div className="membership-controls">
            <Form.Group controlId="membership-round" className="membership-round-selector">
              <Form.Label>Round</Form.Label>
              <Form.Control as="select" value={selectedRoundID} disabled={loading || rounds.length === 0} onChange={(event) => setRoundID(event.target.value)}>
                {rounds.length === 0 && <option value="">No rounds available</option>}
                {rounds.map((round) => <option key={round.roundID} value={round.roundID}>{round.roundName || round.roundID}</option>)}
              </Form.Control>
            </Form.Group>
            <Button variant="outline-secondary" disabled={loading} title="Refresh membership" aria-label="Refresh membership" onClick={() => setReload((value) => value + 1)}>
              <i className="fas fa-sync-alt" aria-hidden="true" />
            </Button>
          </div>
    }>
        {loading && <p role="status">Loading your membership information...</p>}
        {!loading && error && <Alert variant="danger" role="alert">{error}</Alert>}
        {!loading && !error && memberships.length === 0 && <Alert variant="info">No memberships are linked to your account.</Alert>}
        {!loading && !error && memberships.length > 0 && rounds.length === 0 && <Alert variant="info">No non-pending rounds are available for your memberships.</Alert>}
        {!loading && !error && visibleMemberships.map((member) => (
          <section className="membership-round" key={member.memberID} aria-label={member.roundName || member.roundID}>
            <h2>{member.roundName || member.roundID}</h2>
            <dl className="membership-facts">
              <div><dt>Member</dt><dd>{[member.firstName, member.middleName, member.lastName].filter(Boolean).join(" ") || member.userID}</dd></div>
              <div><dt>Membership ID</dt><dd>{member.memberID}</dd></div>
              <div><dt>Membership status</dt><dd>{member.memberStatus || "Not specified"}</dd></div>
              <div><dt>Round status</dt><dd>{statusOf(member)}</dd></div>
              <div><dt>Shares</dt><dd>{numb2(member.numberOfShares)}</dd></div>
              <div><dt>Value per share</dt><dd>{member.currency} {numb2(member.sharesValue)}</dd></div>
              <div><dt>Shareholding value</dt><dd>{member.currency} {numb2(Number(member.numberOfShares || 0) * Number(member.sharesValue || 0))}</dd></div>
            </dl>
            <Amounts member={member} rows={contributionRows} title="Contributions" />
            <Amounts member={member} rows={penaltyRows} title="Penalties" />
            <div className="membership-table-wrap">
              <Table responsive className="membership-amounts membership-distributions">
                <caption>Distributions ({member.currency || "Currency not specified"})</caption>
                <thead><tr><th scope="col">Entitlement</th><th scope="col">Received</th><th scope="col">Remaining to receive</th><th scope="col">Status</th></tr></thead>
                <tbody><tr><td>{numb2(member.distributionAmount)}</td><td>{numb2(member.distributionPaidAmount)}</td><td>{numb2(member.distributionBalanceAmount)}</td><td>{member.distributionStatus || "Not specified"}</td></tr></tbody>
              </Table>
            </div>
          </section>
        ))}
    </WorkspacePage>
  );
}